import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calendar, Clock, User, MapPin, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import FormInput from '../components/FormInput';
import api from '../components/api';

const EditExamenPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    libelle: '',
    description: '',
    classe: '',
    date_debut: '',
    date_fin: '',
    status: 'PROGRAMME'
  });
  const [sessions, setSessions] = useState([]);

  // Options et données de référence
  const [classes, setClasses] = useState([]);
  const [modules, setModules] = useState([]);
  const [salles, setSalles] = useState([]);
  const [surveillants, setSurveillants] = useState([]); // Changé de utilisateurs à surveillants

  const statusOptions = [
    { value: 'PROGRAMME', label: 'Programmé' },
    { value: 'EN_COURS', label: 'En cours' },
    { value: 'TERMINE', label: 'Terminé' },
    { value: 'ANNULE', label: 'Annulé' }
  ];

  useEffect(() => {
    fetchReferenceData();
    fetchExamenData();
  }, [id]);

  useEffect(() => {
    if (formData.classe) {
      fetchModules(formData.classe);
    }
  }, [formData.classe]);

  const fetchExamenData = async () => {
    try {
      const [examenRes, sessionsRes] = await Promise.all([
        api.get(`/examen/examens/${id}/`),
        api.get(`/examen/examens/${id}/sessions/`)
      ]);

      if (examenRes.data.status === 'success') {
        const examen = examenRes.data.data;
        setFormData({
          libelle: examen.libelle || '',
          description: examen.description || '',
          classe: examen.classe?.id || '',
          date_debut: examen.date_debut || '',
          date_fin: examen.date_fin || '',
          status: examen.status || 'PROGRAMME'
        });
      }

      if (sessionsRes.data.status === 'success') {
        // Regrouper les sessions par date
        const sessionsGrouped = {};
        sessionsRes.data.data.forEach(session => {
          const date = session.date_examen;
          if (!sessionsGrouped[date]) {
            sessionsGrouped[date] = {
              date_examen: date,
              creneaux: []
            };
          }
          
          // Transformer les surveillants du backend vers le format frontend
          let surveillantPrincipalId = '';
          let surveillantSecondaireId = '';
          
          if (session.surveillant_principal) {
            surveillantPrincipalId = `${session.surveillant_principal.type}_${session.surveillant_principal.id}`;
          }
          
          if (session.surveillant_secondaire) {
            surveillantSecondaireId = `${session.surveillant_secondaire.type}_${session.surveillant_secondaire.id}`;
          }
          
          sessionsGrouped[date].creneaux.push({
            id: session.id,
            heure_debut: session.heure_debut || '',
            heure_fin: session.heure_fin || '',
            module: session.module?.module?.id || '',
            salle: session.salle?.id || '',
            surveillant_principal: surveillantPrincipalId,
            surveillant_secondaire: surveillantSecondaireId, // Ajouté
            consignes: session.consignes || ''
          });
        });
        
        const sessionsArray = Object.values(sessionsGrouped);
        setSessions(sessionsArray);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement de l\'examen');
    } finally {
      setLoadingData(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [classesRes, sallesRes, enseignantsRes, personnelRes] = await Promise.all([
        api.get('/classe/get-all-classe/'),
        api.get('/etablissement/salles/'),
        api.get('/enseignant/liste-enseignant/'),  // URL corrigée
        api.get('/etablissement/personnels/')      // URL corrigée
      ]);

      // Classes
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      
      // Salles
      const sallesData = sallesRes.data;
      if (Array.isArray(sallesData)) {
        setSalles(sallesData);
      } else if (sallesData && Array.isArray(sallesData.results)) {
        setSalles(sallesData.results);
      } else if (sallesData && typeof sallesData === 'object') {
        setSalles(Object.values(sallesData));
      } else {
        setSalles([]);
      }

      // Combiner enseignants et personnel pour les surveillants
      // Gestion des différents formats de réponse API
      let enseignants = [];
      if (enseignantsRes.data) {
        if (Array.isArray(enseignantsRes.data)) {
          enseignants = enseignantsRes.data;
        } else if (enseignantsRes.data.results && Array.isArray(enseignantsRes.data.results)) {
          enseignants = enseignantsRes.data.results;
        } else if (enseignantsRes.data.data && Array.isArray(enseignantsRes.data.data)) {
          enseignants = enseignantsRes.data.data;
        }
      }
      
      let personnel = [];
      if (personnelRes.data) {
        if (Array.isArray(personnelRes.data)) {
          personnel = personnelRes.data;
        } else if (personnelRes.data.results && Array.isArray(personnelRes.data.results)) {
          personnel = personnelRes.data.results;
        } else if (personnelRes.data.data && Array.isArray(personnelRes.data.data)) {
          personnel = personnelRes.data.data;
        }
      }

      // Créer une liste combinée avec indication du type
      const surveillantsCombines = [
        ...enseignants.map(ens => ({
          id: `enseignant_${ens.id}`,
          type: 'enseignant',
          original_id: ens.id,
          nom: ens.nom,
          prenom: ens.prenom,
          label: `${ens.nom} ${ens.prenom} (Enseignant)`
        })),
        ...personnel.map(pers => ({
          id: `personnel_${pers.id}`,
          type: 'personnel', 
          original_id: pers.id,
          nom: pers.nom,
          prenom: pers.prenom,
          label: `${pers.nom} ${pers.prenom} (Personnel)`
        }))
      ];

      setSurveillants(surveillantsCombines);

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données de référence');
      // Initialiser avec des tableaux vides en cas d'erreur
      setClasses([]);
      setSalles([]);
      setSurveillants([]);
    }
  };

  const fetchModules = async (classeId) => {
    try {
      const response = await api.get(`/emplois/modules-by-classe/${classeId}/`);
      setModules(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des modules:', error);
      toast.error('Erreur lors du chargement des modules');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSessionChange = (sessionIndex, field, value) => {
    const updatedSessions = [...sessions];
    updatedSessions[sessionIndex] = {
      ...updatedSessions[sessionIndex],
      [field]: value
    };
    setSessions(updatedSessions);
  };

  const handleCreneauChange = (sessionIndex, creneauIndex, field, value) => {
    const updatedSessions = [...sessions];
    updatedSessions[sessionIndex].creneaux[creneauIndex] = {
      ...updatedSessions[sessionIndex].creneaux[creneauIndex],
      [field]: value
    };
    setSessions(updatedSessions);
  };

  const addSession = () => {
    const newSession = {
      date_examen: '',
      creneaux: [{
        heure_debut: '',
        heure_fin: '',
        module: '',
        salle: '',
        surveillant_principal: '',
        surveillant_secondaire: '', // Ajouté
        consignes: ''
      }]
    };
    setSessions([...sessions, newSession]);
  };

  const addCreneau = (sessionIndex) => {
    const updatedSessions = [...sessions];
    updatedSessions[sessionIndex].creneaux.push({
      heure_debut: '',
      heure_fin: '',
      module: '',
      salle: '',
      surveillant_principal: '',
      surveillant_secondaire: '', // Ajouté
      consignes: ''
    });
    setSessions(updatedSessions);
  };

  const removeCreneau = (sessionIndex, creneauIndex) => {
    const updatedSessions = [...sessions];
    if (updatedSessions[sessionIndex].creneaux.length > 1) {
      updatedSessions[sessionIndex].creneaux.splice(creneauIndex, 1);
      setSessions(updatedSessions);
    }
  };

  const removeSession = (index) => {
    if (sessions.length > 1) {
      setSessions(sessions.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    if (!formData.libelle.trim()) {
      toast.error('Le libellé est requis');
      return false;
    }
    if (!formData.classe) {
      toast.error('La classe est requise');
      return false;
    }
    if (!formData.date_debut || !formData.date_fin) {
      toast.error('Les dates de début et fin sont requises');
      return false;
    }
    if (new Date(formData.date_fin) < new Date(formData.date_debut)) {
      toast.error('La date de fin doit être postérieure à la date de début');
      return false;
    }

    // Validation des sessions et créneaux
    for (let i = 0; i < sessions.length; i++) {
      const session = sessions[i];
      if (!session.date_examen) {
        toast.error(`Session ${i + 1}: La date est requise`);
        return false;
      }
      
      // Validation des créneaux
      for (let j = 0; j < session.creneaux.length; j++) {
        const creneau = session.creneaux[j];
        if (!creneau.heure_debut || !creneau.heure_fin || !creneau.module) {
          toast.error(`Session ${i + 1}, Créneau ${j + 1}: Les heures et le module sont obligatoires`);
          return false;
        }
        if (creneau.heure_fin <= creneau.heure_debut) {
          toast.error(`Session ${i + 1}, Créneau ${j + 1}: L'heure de fin doit être après l'heure de début`);
          return false;
        }
      }

      // Vérification des conflits d'horaires dans la même session
      for (let j = 0; j < session.creneaux.length; j++) {
        for (let k = j + 1; k < session.creneaux.length; k++) {
          const creneau1 = session.creneaux[j];
          const creneau2 = session.creneaux[k];
          
          if (creneau1.salle && creneau2.salle && creneau1.salle === creneau2.salle) {
            const debut1 = creneau1.heure_debut;
            const fin1 = creneau1.heure_fin;
            const debut2 = creneau2.heure_debut;
            const fin2 = creneau2.heure_fin;
            
            if ((debut1 < fin2 && fin1 > debut2)) {
              const salleName = salles.find(s => s.id == creneau1.salle)?.libelle || 'Salle inconnue';
              toast.error(`❌ Conflit d'horaires : Les créneaux ${j + 1} et ${k + 1} de la session ${i + 1} utilisent la même salle "${salleName}" avec des horaires qui se chevauchent`);
              return false;
            }
          }
        }
      }
    }

    // Information sur les créneaux multiples
    const totalCreneaux = sessions.reduce((total, session) => total + session.creneaux.length, 0);
    console.log(`ℹ️ ${totalCreneaux} créneaux programmés au total`);
    sessions.forEach((session, index) => {
      if (session.creneaux.length > 1) {
        console.log(`ℹ️ Session ${index + 1} (${session.date_examen}): ${session.creneaux.length} créneaux`);
      }
    });

    return true;
  };

  // Fonction pour transformer les surveillants pour l'API (comme dans CreateExamenPage)
  const transformSurveillantForAPI = (surveillantId) => {
    if (!surveillantId) return null;
    
    const surveillant = surveillants.find(s => s.id === surveillantId);
    if (!surveillant) return null;

    return {
      type: surveillant.type,
      id: surveillant.original_id
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Transformer les sessions avec créneaux en sessions individuelles pour le backend
      const backendSessions = [];
      sessions.forEach(session => {
        session.creneaux.forEach(creneau => {
          const sessionData = {
            date_examen: session.date_examen,
            heure_debut: creneau.heure_debut,
            heure_fin: creneau.heure_fin,
            module: creneau.module,
            salle: creneau.salle,
            consignes: creneau.consignes
          };

          // Traitement des surveillants
          if (creneau.surveillant_principal) {
            const surveillantPrincipal = transformSurveillantForAPI(creneau.surveillant_principal);
            if (surveillantPrincipal) {
              sessionData.surveillant_principal_type = surveillantPrincipal.type;
              sessionData.surveillant_principal_id = surveillantPrincipal.id;
            }
          }

          if (creneau.surveillant_secondaire) {
            const surveillantSecondaire = transformSurveillantForAPI(creneau.surveillant_secondaire);
            if (surveillantSecondaire) {
              sessionData.surveillant_secondaire_type = surveillantSecondaire.type;
              sessionData.surveillant_secondaire_id = surveillantSecondaire.id;
            }
          }

          backendSessions.push(sessionData);
        });
      });

      const payload = {
        ...formData,
        sessions: backendSessions
      };

      const response = await api.put(`/examen/examens/${id}/`, payload);
      
      if (response.data.status === 'success') {
        toast.success('Examen modifié avec succès');
        navigate(`/examens/${id}`);
      } else {
        toast.error(response.data.message || 'Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/examens/${id}`)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Modifier l'Examen</h1>
            <p className="text-gray-600">Modifiez les informations de l'examen</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations générales */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Informations générales
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <FormInput
                  label="Libellé de l'examen"
                  name="libelle"
                  value={formData.libelle}
                  onChange={handleInputChange}
                  placeholder="Ex: Examen Final Semestre 1"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <FormInput
                  label="Description"
                  name="description"
                  type="textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Description de la session d'examen..."
                  rows={3}
                />
              </div>

              <div>
                <FormInput
                  label="Classe"
                  name="classe"
                  type="select"
                  value={formData.classe}
                  onChange={handleInputChange}
                  options={[
                    { value: '', label: 'Sélectionner une classe' },
                    ...classes.map(classe => ({
                      value: classe.id,
                      label: classe.libelle
                    }))
                  ]}
                  required
                />
              </div>

              <div>
                <FormInput
                  label="Date de début"
                  name="date_debut"
                  type="date"
                  value={formData.date_debut}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <FormInput
                  label="Date de fin"
                  name="date_fin"
                  type="date"
                  value={formData.date_fin}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <FormInput
                  label="Statut"
                  name="status"
                  type="select"
                  value={formData.status}
                  onChange={handleInputChange}
                  options={statusOptions.map(option => ({
                    value: option.value,
                    label: option.label
                  }))}
                />
              </div>
            </div>
          </div>

          {/* Sessions d'examen */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Sessions d'examen
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Chaque session = une date d'examen. Dans chaque session, vous pouvez ajouter plusieurs créneaux (modules à des heures différentes)
                </p>
              </div>
              <button
                type="button"
                onClick={addSession}
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter une session
              </button>
            </div>

            <div className="space-y-6">
              {sessions.map((session, sessionIndex) => (
                <div key={sessionIndex} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-gray-900">
                        Session {sessionIndex + 1}
                        {session.date_examen && (
                          <span className="text-sm text-gray-500 ml-2">
                            ({new Date(session.date_examen).toLocaleDateString('fr-FR')})
                          </span>
                        )}
                      </h3>
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                        {session.creneaux.length} créneau{session.creneaux.length > 1 ? 'x' : ''}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={addSession}
                        className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50"
                        title="Ajouter une nouvelle session (date différente)"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      {sessions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSession(sessionIndex)}
                          className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                          title="Supprimer cette session"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Date de la session */}
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <FormInput
                        label="Date de la session"
                        type="date"
                        value={session.date_examen}
                        onChange={(e) => handleSessionChange(sessionIndex, 'date_examen', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Créneaux horaires */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-gray-800">Créneaux horaires</h4>
                      <button
                        type="button"
                        onClick={() => addCreneau(sessionIndex)}
                        className="inline-flex items-center px-2 py-1 text-sm bg-[#202654] text-white rounded-lg hover:bg-[#1a1f4a] transition-colors"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Ajouter un créneau
                      </button>
                    </div>

                    {session.creneaux.map((creneau, creneauIndex) => (
                      <div key={creneauIndex} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center space-x-2">
                            <h5 className="font-medium text-gray-700">Créneau {creneauIndex + 1}</h5>
                            {creneau.module && modules.find(m => m.id == creneau.module) && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                {modules.find(m => m.id == creneau.module)?.libelle}
                              </span>
                            )}
                            {creneau.heure_debut && creneau.heure_fin && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                {creneau.heure_debut} - {creneau.heure_fin}
                              </span>
                            )}
                          </div>
                          {session.creneaux.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCreneau(sessionIndex, creneauIndex)}
                              className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                              title="Supprimer ce créneau"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <FormInput
                              label="Heure de début"
                              type="time"
                              value={creneau.heure_debut}
                              onChange={(e) => handleCreneauChange(sessionIndex, creneauIndex, 'heure_debut', e.target.value)}
                              required
                            />
                          </div>

                          <div>
                            <FormInput
                              label="Heure de fin"
                              type="time"
                              value={creneau.heure_fin}
                              onChange={(e) => handleCreneauChange(sessionIndex, creneauIndex, 'heure_fin', e.target.value)}
                              required
                            />
                          </div>

                          <div>
                            <FormInput
                              label="Module"
                              type="select"
                              value={creneau.module}
                              onChange={(e) => handleCreneauChange(sessionIndex, creneauIndex, 'module', e.target.value)}
                              options={[
                                { value: '', label: 'Sélectionner un module' },
                                ...modules.map(module => ({
                                  value: module.id,
                                  label: module.libelle
                                }))
                              ]}
                              required
                            />
                          </div>

                          <div>
                            <FormInput
                              label="Salle"
                              type="select"
                              value={creneau.salle}
                              onChange={(e) => handleCreneauChange(sessionIndex, creneauIndex, 'salle', e.target.value)}
                              options={[
                                { value: '', label: 'Sélectionner une salle' },
                                ...salles.map(salle => ({
                                  value: salle.id,
                                  label: `${salle.libelle} (${salle.capacite} places)`
                                }))
                              ]}
                            />
                          </div>

                          <div>
                            <FormInput
                              label="Surveillant principal"
                              type="select"
                              value={creneau.surveillant_principal}
                              onChange={(e) => handleCreneauChange(sessionIndex, creneauIndex, 'surveillant_principal', e.target.value)}
                              options={[
                                { value: '', label: 'Sélectionner un surveillant' },
                                ...surveillants.map(surveillant => ({
                                  value: surveillant.id,
                                  label: surveillant.label
                                }))
                              ]}
                            />
                          </div>

                          <div>
                            <FormInput
                              label="Surveillant secondaire"
                              type="select"
                              value={creneau.surveillant_secondaire}
                              onChange={(e) => handleCreneauChange(sessionIndex, creneauIndex, 'surveillant_secondaire', e.target.value)}
                              options={[
                                { value: '', label: 'Aucun surveillant secondaire' },
                                ...surveillants.map(surveillant => ({
                                  value: surveillant.id,
                                  label: surveillant.label
                                }))
                              ]}
                            />
                          </div>

                          <div className="md:col-span-3">
                            <FormInput
                              label="Consignes particulières"
                              type="textarea"
                              value={creneau.consignes}
                              onChange={(e) => handleCreneauChange(sessionIndex, creneauIndex, 'consignes', e.target.value)}
                              placeholder="Consignes particulières pour ce créneau..."
                              rows={2}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/examens/${id}`)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading && <LoadingSpinner size="sm" className="mr-2" />}
              Modifier l'examen
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditExamenPage; 